import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { visitorsAPI, usersAPI, flatsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper, 
  Dialog, DialogTitle, DialogContent, Button, TextField, MenuItem, Alert,
  useTheme, useMediaQuery, Card, CardContent, Chip, TableContainer, Avatar, InputAdornment, Stack, Divider,
  Checkbox, FormControlLabel, DialogActions, IconButton, Pagination, Autocomplete,
  FormControl, FormHelperText, FormLabel, Radio, RadioGroup
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  PeopleAlt as VisitorsIcon,
  HomeWork as FlatIcon,
  Phone as PhoneIcon,
  DirectionsCar as VehicleIcon,
  Badge as IdIcon,
  Notes as NotesIcon,
  Schedule as TimeIcon,
  CameraAlt as CameraIcon
  ,LocalParking as ParkingIcon,
  Delete as DeleteIcon,
  Groups as StandardVisitorIcon,
  LocalShipping as DeliveryIcon,
  Handyman as ServiceProviderIcon,
  Hotel as TemporaryResidentIcon,
  MoreHoriz as OtherPurposeIcon,
  CheckCircle as SelectedPurposeIcon
} from "@mui/icons-material";
//import VisitorDialog from "./VisitorDialog";

// Cross-platform camera implementation
const initializeCamera = async () => {
  // Check if we're in a mobile app environment (Capacitor)
  if (window.Capacitor) {
    // Import Capacitor modules dynamically when available
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    const { Capacitor } = await import('@capacitor/core');
    return {
      Camera,
      CameraResultType,
      CameraSource,
      Capacitor,
      isNative: true
    };
  } else {
    // Web implementation
    return {
      Camera: {
        checkPermissions: async () => ({ camera: 'granted' }),
        requestPermissions: async () => ({ camera: 'granted' }),
        getPhoto: async (options) => {
          throw new Error('Use web camera implementation instead');
        }
      },
      CameraResultType: {
        Uri: 'uri',
        Base64: 'base64',
        DataUrl: 'dataUrl'
      },
      CameraSource: {
        Camera: 'camera',
        Photos: 'photos'
      },
      Capacitor: {
        isNativePlatform: () => false
      },
      isNative: false
    };
  }
};

const VisitorDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Determine user groups and permissions
  const groups = user?.groups || [];
  
  // Extract group names - handle both old format (strings) and new format (objects)
  const groupNames = groups.map(g => typeof g === 'string' ? g : g.name);
  
  // Check both groups and direct role property
  const isBuildingAdmin = groupNames.includes('building_admin') || user?.role === 'building_admin';
  const isMaintenanceStaff = groupNames.includes('maintenance_staff') || user?.role === 'maintenance_staff';
  const isOwner = groupNames.includes('owner') || user?.role === 'resident';
  const isTenant = groupNames.includes('tenant') || user?.role === 'resident';
  const canChangeApproval = isOwner || isTenant;
  const canChangeVisitStatus = isMaintenanceStaff;
  const canUseCamera = isBuildingAdmin || isMaintenanceStaff;
  const canUseLeaveAtGate = canChangeApproval;
  const normalizeVisitStatus = (value) => {
    const normalized = String(value || 'PENDING').trim().toUpperCase().replace(/[ -]+/g, '_');
    if (['IN', 'CHECKED_IN'].includes(normalized)) return 'IN';
    if (['OUT', 'CHECKED_OUT'].includes(normalized)) return 'OUT';
    if (normalized === 'LEFT_AT_GATE') return 'LEFT_AT_GATE';
    return normalized;
  };
  const getVisitStatusPresentation = (value) => {
    const normalized = normalizeVisitStatus(value);
    if (normalized === 'IN') return { label: 'In', color: 'success' };
    if (normalized === 'OUT') return { label: 'Out', color: 'default' };
    if (normalized === 'LEFT_AT_GATE') return { label: 'Left at Gate', color: 'info' };
    if (normalized === 'CREATED') return { label: 'Created', color: 'default' };
    return { label: String(value || 'Pending').replace(/_/g, ' '), color: 'default' };
  };
  const getApprovalStatusPresentation = (value) => {
    const normalized = String(value || 'PENDING').trim().toUpperCase().replace(/[ -]+/g, '_');
    if (normalized === 'APPROVED') return { label: 'Approved', color: 'success' };
    if (normalized === 'REJECTED') return { label: 'Rejected', color: 'error' };
    if (normalized === 'LEAVE_AT_GATE') return { label: 'Leave at Gate', color: 'info' };
    return { label: 'Pending', color: 'warning' };
  };
  const formatVisitorDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleString([], {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  };
  
  const [buildingId, setBuildingId] = useState("");
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState(null);
  const [editError, setEditError] = useState('');
  const [editing, setEditing] = useState(false);
  const [flats, setFlats] = useState([]);
  const [parkingSlots, setParkingSlots] = useState([]);
  const [parkingDialogOpen, setParkingDialogOpen] = useState(false);
  const [newSlotNumber, setNewSlotNumber] = useState('');
  const [newSlotLocation, setNewSlotLocation] = useState('');
  const [parkingError, setParkingError] = useState('');
  const [approvalError, setApprovalError] = useState('');
  const [approvalUpdatingId, setApprovalUpdatingId] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const today = new Date();
  const fifteenDaysAgo = new Date(today);
  fifteenDaysAgo.setDate(today.getDate() - 14);
  const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [startDate, setStartDate] = useState(formatDateInput(fifteenDaysAgo));
  const [endDate, setEndDate] = useState(formatDateInput(today));
  const [page, setPage] = useState(1);
  const visitorsPerPage = 10;
  const hasInitialized = useRef(false);
  const associatedFlatIdsRef = useRef([]);

  // Camera platform state
  const [cameraAPI, setCameraAPI] = useState(null);
  const [cameraInitialized, setCameraInitialized] = useState(false);

  // Reusable function to refresh visitors data
  const refreshVisitors = useCallback(async (buildingIdToUse) => {
    if (!buildingIdToUse) return;
    try {
      let visitorData = [];

      if (isBuildingAdmin || isMaintenanceStaff) {
        console.log("VisitorDashboard: Refreshing active visitors for building:", buildingIdToUse);
        const res = await visitorsAPI.getByBuilding(buildingIdToUse);
        visitorData = res.data || [];
      } else {
        const flatIds = associatedFlatIdsRef.current;
        console.log("VisitorDashboard: Refreshing active visitors for associated flats:", flatIds);
        const responses = await Promise.all(flatIds.map(flatId => visitorsAPI.getByFlat(flatId)));
        visitorData = responses.flatMap(response => response.data || []);
      }

      // A user can have duplicate associations; ensure each visitor is shown once.
      const uniqueVisitors = Array.from(
        new Map(visitorData.map(visitor => [visitor.visitorId, visitor])).values()
      );
      setVisitors(uniqueVisitors);
    } catch (err) {
      console.error("VisitorDashboard: Error refreshing visitors:", err);
      setVisitors([]);
    }
  }, [isBuildingAdmin, isMaintenanceStaff]);

  const refreshParkingSlots = useCallback(async (buildingIdToUse) => {
    if (!buildingIdToUse) return;
    try {
      const response = await visitorsAPI.getParkingSlots(buildingIdToUse);
      setParkingSlots(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load visitor parking slots:', error);
      setParkingSlots([]);
    }
  }, []);

  useEffect(() => {
    refreshParkingSlots(buildingId);
  }, [buildingId, refreshParkingSlots]);

  // Initialize camera platform
  useEffect(() => {
    const initCamera = async () => {
      try {
        const camera = await initializeCamera();
        setCameraAPI(camera);
        setCameraInitialized(true);
        console.log('Camera platform initialized:', camera.isNative ? 'Native' : 'Web');
      } catch (error) {
        console.error('Failed to initialize camera:', error);
        setCameraInitialized(false);
      }
    };
    
    initCamera();
  }, []);

  useEffect(() => {
    const fetchBuildingAssociation = async () => {
      const userId = user?.attributes?.sub;
      console.log("VisitorDashboard: userId =", userId);
      if (!userId) {
        console.log("VisitorDashboard: No userId found");
        setLoading(false);
        hasInitialized.current = false; // Reset when no user
        return;
      }
      
      // Prevent double loading for the same user
      if (hasInitialized.current) {
        console.log("VisitorDashboard: Already initialized, skipping");
        return;
      }
      
      try {
        hasInitialized.current = true;
        setLoading(true);
        console.log("VisitorDashboard: Fetching associations for userId:", userId);
        const res = await usersAPI.getAssociations(userId);
        const buildingAssoc = res.data || [];
        console.log("VisitorDashboard: Building associations:", buildingAssoc);
        const ids = buildingAssoc.map(item => item.buildingId);
        associatedFlatIdsRef.current = Array.from(new Set(
          buildingAssoc.filter(assoc => assoc.flatId).map(assoc => assoc.flatId)
        ));
        console.log("VisitorDashboard: Building IDs:", ids);
        
        if (ids && ids.length > 0) {
          setBuildingId(ids[0]);
          console.log("VisitorDashboard: Set buildingId to:", ids[0]);
          
          // Fetch flats data based on user role
          try {
            console.log("VisitorDashboard: Building associations:", buildingAssoc);
            let flatsData = [];
            
            if (isBuildingAdmin || isMaintenanceStaff) {
              // Building admin and maintenance staff can see all flats in the building
              const flatsRes = await flatsAPI.getByBuilding(ids[0]);
              flatsData = flatsRes.data || [];
              console.log("VisitorDashboard: Building admin/maintenance staff - flats loaded:", flatsData.length);
            } else {
              // For owners/tenants, use user associations to get their flats
              // Get flat IDs from user associations
              const userFlatIds = associatedFlatIdsRef.current;
              
             
              if (userFlatIds.length > 0) {
                // Fetch all flats and filter by user's associated flat IDs
                const flatsRes = await flatsAPI.getByBuilding(ids[0]);
                const allFlats = flatsRes.data || [];
                console.log("VisitorDashboard: All flats in building:", allFlats.length);
                
                flatsData = allFlats.filter(flat => userFlatIds.includes(flat.flatId));
                console.log("VisitorDashboard: Filtered flats by associations:", flatsData.length);
                
                // Debug the filtering
                flatsData.forEach((flat, index) => {
                  console.log(`VisitorDashboard: User's Flat ${index + 1}:`, {
                    flatId: flat.flatId,
                    flatNumber: flat.flatNumber,
                    blockName: flat.blockName
                  });
                });
              } else {
                console.log("VisitorDashboard: No flat IDs found in user associations");
                flatsData = [];
              }
            }
            
            setFlats(flatsData);
            console.log("VisitorDashboard: Final flats set:", flatsData);
          } catch (err) {
            console.error("VisitorDashboard: Error fetching flats:", err);
            setFlats([]);
          }
          
          // Fetch visitors data using the reusable function
          await refreshVisitors(ids[0]);
        } else {
          console.log("VisitorDashboard: No building IDs found");
          setBuildingId("");
          setVisitors([]);
          setFlats([]);
          associatedFlatIdsRef.current = [];
        }
      } catch (err) {
        console.error("VisitorDashboard: Error fetching building associations:", err);
        setBuildingId("");
        setVisitors([]);
        setFlats([]);
        associatedFlatIdsRef.current = [];
        hasInitialized.current = false; // Reset on error so it can retry
      } finally {
        setLoading(false);
      }
    };
    
    fetchBuildingAssociation();    
  }, [user?.attributes?.sub, isBuildingAdmin, isMaintenanceStaff, refreshVisitors]);

  const filteredVisitors = useMemo(() => {
    const rangeStart = new Date(`${startDate}T00:00:00`).getTime();
    const rangeEnd = new Date(`${endDate}T23:59:59.999`).getTime();
    const getVisitorTimestamp = (visitor) =>
      Date.parse(visitor.inTime || visitor.visitStartTime || visitor.createdAt || '');

    return visitors
      .filter(visitor => {
        let timestamp = getVisitorTimestamp(visitor);
        if (Number.isNaN(timestamp) && /^\d{2}-\d{2}-\d{4}$/.test(visitor.visitDate || '')) {
          const [day, month, year] = visitor.visitDate.split('-');
          timestamp = new Date(`${year}-${month}-${day}T00:00:00`).getTime();
        }
        return !Number.isNaN(timestamp) && timestamp >= rangeStart && timestamp <= rangeEnd;
      })
      .sort((a, b) => getVisitorTimestamp(b) - getVisitorTimestamp(a));
  }, [visitors, startDate, endDate]);

  const pageCount = Math.max(1, Math.ceil(filteredVisitors.length / visitorsPerPage));
  const paginatedVisitors = filteredVisitors.slice((page - 1) * visitorsPerPage, page * visitorsPerPage);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, visitors.length]);

  const handleStartDateChange = (value) => {
    const selectedStart = new Date(`${value}T00:00:00`);
    const selectedEnd = new Date(`${endDate}T00:00:00`);
    const earliestAllowed = new Date(selectedEnd);
    earliestAllowed.setMonth(earliestAllowed.getMonth() - 6);
    setStartDate(formatDateInput(selectedStart < earliestAllowed ? earliestAllowed : selectedStart));
  };

  const handleEndDateChange = (value) => {
    const selectedEnd = new Date(`${value}T00:00:00`);
    const selectedStart = new Date(`${startDate}T00:00:00`);
    const latestAllowed = new Date(selectedStart);
    latestAllowed.setMonth(latestAllowed.getMonth() + 6);
    const cappedEnd = selectedEnd > today ? today : selectedEnd;
    setEndDate(formatDateInput(cappedEnd > latestAllowed ? latestAllowed : cappedEnd));
  };

  const handleApprovalChange = async (visitor, approvalStatus) => {
    const visitorId = visitor.visitorId || visitor.visitorID;
    if (!buildingId || !visitorId || !canChangeApproval) return;

    setApprovalError('');
    setApprovalUpdatingId(visitorId);
    try {
      await visitorsAPI.update(buildingId, visitorId, { approvalStatus });
      setVisitors(current => current.map(item =>
        (item.visitorId || item.visitorID) === visitorId
          ? {
              ...item,
              approvalStatus,
              ...(String(approvalStatus).toUpperCase().replace(/[ -]+/g, '_') === 'LEAVE_AT_GATE'
                ? {
                    status: 'LEFT_AT_GATE',
                    leftAtGateAt: new Date().toISOString(),
                    expiryStatus: 'CLOSED',
                  }
                : {})
            }
          : item
      ));
      await refreshParkingSlots(buildingId);
    } catch (error) {
      setApprovalError(error.response?.data?.message || 'Failed to update visitor approval status');
    } finally {
      setApprovalUpdatingId(null);
    }
  };

  const renderApprovalControls = (visitor, compact = false) => {
    if (!canChangeApproval) return null;
    const visitorId = visitor.visitorId || visitor.visitorID;
    const currentStatus = String(visitor.approvalStatus || 'PENDING').toUpperCase().replace(/[ -]+/g, '_');
    if (currentStatus !== 'PENDING') return null;
    const updating = approvalUpdatingId === visitorId;
    const statuses = [
      { value: 'APPROVED', label: 'Approve', color: 'success' },
      { value: 'REJECTED', label: 'Reject', color: 'error' },
    ];
    if (canUseLeaveAtGate && visitor.purpose === 'Delivery Personnel') {
      statuses.push({ value: 'LEAVE_AT_GATE', apiValue: 'Leave At Gate', label: 'Leave at Gate', color: 'info' });
    }

    return (
      <Stack
        direction={compact ? 'row' : 'column'}
        spacing={0.5}
        sx={{
          mt: 0.75,
          width: compact ? 'auto' : 112,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: compact ? 0 : 'auto',
          flexWrap: compact ? 'wrap' : 'nowrap',
          gap: 0.5,
          alignItems: compact ? 'center' : 'stretch',
        }}
      >
        {statuses.map(status => (
          <Button
            key={status.value}
            size="small"
            color={status.color}
            variant={currentStatus === status.value ? 'contained' : 'outlined'}
            disabled={updating || currentStatus === status.value}
            onClick={() => handleApprovalChange(visitor, status.apiValue || status.value)}
            sx={{
              minWidth: compact ? 58 : 0,
              width: compact ? 'auto' : '100%',
              px: compact ? 0.75 : 1,
              py: compact ? 0.25 : 0.5,
              fontSize: compact ? '0.65rem' : '0.72rem',
              lineHeight: 1.5,
              whiteSpace: 'nowrap',
              fontWeight: 700,
            }}
          >
            {status.label}
          </Button>
        ))}
      </Stack>
    );
  };

  const handleVisitStatusChange = async (visitor, status) => {
    const visitorId = visitor.visitorId || visitor.visitorID;
    if (!buildingId || !visitorId || !canChangeVisitStatus) return;

    setStatusError('');
    setStatusUpdatingId(visitorId);
    const update = status === 'OUT'
      ? { status, outTime: new Date().toISOString() }
      : { status };
    try {
      await visitorsAPI.update(buildingId, visitorId, update);
      setVisitors(current => current.map(item =>
        (item.visitorId || item.visitorID) === visitorId
          ? { ...item, ...update }
          : item
      ));
      await refreshParkingSlots(buildingId);
    } catch (error) {
      setStatusError(error.response?.data?.message || `Failed to mark visitor ${status === 'OUT' ? 'out' : 'in'}`);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const renderVisitStatusControls = (visitor, compact = false) => {
    if (!canChangeVisitStatus) return null;
    const visitorId = visitor.visitorId || visitor.visitorID;
    const currentStatus = normalizeVisitStatus(visitor.status);
    if (['OUT', 'LEFT_AT_GATE'].includes(currentStatus)) return null;
    const approvalStatus = String(visitor.approvalStatus || 'PENDING').toUpperCase().replace(/[ -]+/g, '_');
    if (currentStatus !== 'IN' && approvalStatus !== 'APPROVED') return null;
    const updating = statusUpdatingId === visitorId;
    const statuses = currentStatus === 'IN'
      ? [{ value: 'OUT', label: 'Check Out', color: 'error' }]
      : [{ value: 'IN', label: 'Check In', color: 'success' }];

    return (
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
        {statuses.map(status => (
          <Button
            key={status.value}
            size="small"
            color={status.color}
            variant={currentStatus === status.value ? 'contained' : 'outlined'}
            disabled={updating || currentStatus === status.value}
            onClick={() => handleVisitStatusChange(visitor, status.value)}
            sx={{
              minWidth: compact ? 68 : 76,
              px: compact ? 0.75 : 1,
              py: 0.25,
              fontSize: compact ? '0.65rem' : '0.7rem',
              lineHeight: 1.5,
            }}
          >
            {status.label}
          </Button>
        ))}
      </Stack>
    );
  };

  console.log("VisitorDashboard: Current state - buildingId:", buildingId, "flats:", flats, "visitors:", visitors, "loading:", loading, "isBuildingAdmin:", isBuildingAdmin, "isMaintenanceStaff:", isMaintenanceStaff);

  return (
    <Box className="mobile-content-spacing" sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
              <VisitorsIcon color="primary" sx={{ fontSize: { xs: 30, sm: 36 } }} />
              <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, letterSpacing: 0 }}>
                {t('visitor.title')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Manage expected visitors, check-in status, and approval details.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            {buildingId && (
              <>
                <Chip
                  icon={<FlatIcon />}
                  label={(isBuildingAdmin || isMaintenanceStaff) ? `All Flats: ${flats.length}` : `My Flats: ${flats.length}`}
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  icon={<VisitorsIcon />}
                  label={`${t('visitor.visitors')}: ${filteredVisitors.length}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              </>
            )}
            {isBuildingAdmin && (
              <Button variant="outlined" startIcon={<ParkingIcon />} onClick={() => setParkingDialogOpen(true)} sx={{ fontWeight: 700 }}>
                Parking Slots
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)} sx={{ fontWeight: 700 }}>
              {t('visitor.addVisitor')}
            </Button>
          </Stack>
        </Box>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            label="From date"
            type="date"
            size="small"
            value={startDate}
            onChange={(event) => handleStartDateChange(event.target.value)}
            inputProps={{
              min: (() => {
                const minimum = new Date(`${endDate}T00:00:00`);
                minimum.setMonth(minimum.getMonth() - 6);
                return formatDateInput(minimum);
              })(),
              max: endDate
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To date"
            type="date"
            size="small"
            value={endDate}
            onChange={(event) => handleEndDateChange(event.target.value)}
            inputProps={{ min: startDate, max: formatDateInput(today) }}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="body2" color="text.secondary">
            Showing {filteredVisitors.length} visitor(s). Date range is limited to six months.
          </Typography>
        </Stack>
      </Paper>
      {approvalError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setApprovalError('')}>{approvalError}</Alert>}
      {statusError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setStatusError('')}>{statusError}</Alert>}
      {isMobile ? (
        // Mobile Card Layout
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {loading ? (
            <Alert severity="info" sx={{ fontSize: '14px' }}>{t('visitor.loading')}</Alert>
          ) : filteredVisitors.length === 0 ? (
            <Alert severity="info" sx={{ fontSize: '14px' }}>{t('visitor.noActiveVisitors')}</Alert>
          ) : (
            paginatedVisitors.map((v) => (
              <Card key={v.visitorId} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={v.photoUrl || undefined} sx={{ width: 40, height: 40, bgcolor: 'primary.light' }}>
                        {!v.photoUrl && <PersonIcon fontSize="small" />}
                      </Avatar>
                      <Typography
                        variant="subtitle2"
                        sx={{ minWidth: 0, fontWeight: 700, fontSize: '16px', overflowWrap: 'anywhere' }}
                      >
                        {v.visitorName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', pl: 6 }}>
                      <Chip 
                        label={getVisitStatusPresentation(v.status).label}
                        color={getVisitStatusPresentation(v.status).color}
                        size="small" 
                        sx={{ fontSize: '11px', height: 24 }}
                      />
                      {!(
                        normalizeVisitStatus(v.status) === 'LEFT_AT_GATE'
                        && getApprovalStatusPresentation(v.approvalStatus).label === 'Leave at Gate'
                      ) && (
                        <Chip 
                          label={getApprovalStatusPresentation(v.approvalStatus).label}
                          color={getApprovalStatusPresentation(v.approvalStatus).color}
                          size="small" 
                          sx={{ fontSize: '11px', height: 24 }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                    Flat: {v.flatNumber}/{v.blockName}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                    Purpose: {v.purpose}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                    Until: {v.visitEndTime ? new Date(v.visitEndTime).toLocaleString() : '-'}
                    {v.parkingSlotNumber ? ` | Parking: ${v.parkingSlotNumber}` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '11px' }}>
                      In: {formatVisitorDateTime(v.inTime)}
                    </Typography>
                    {v.outTime && (
                      <Typography variant="caption" sx={{ fontSize: '11px' }}>
                        Out: {formatVisitorDateTime(v.outTime)}
                      </Typography>
                    )}
                  </Box>
                  {renderVisitStatusControls(v, true)}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setEditingVisitor(v);
                        setEditDialogOpen(true);
                        setEditError('');
                      }}
                      sx={{ fontSize: '12px', px: 2, fontWeight: 700 }}
                    >
                      Edit
                    </Button>
                  </Box>
                  {renderApprovalControls(v, true)}
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        // Desktop Table Layout
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 2,
            overflow: 'hidden',
            '& .MuiTableCell-head': {
              bgcolor: '#f7f9fc',
              fontWeight: 700,
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('visitor.visitorName')}</TableCell>
                <TableCell>{t('visitor.flatBlock')}</TableCell>
                <TableCell>{t('visitor.purpose')}</TableCell>
                <TableCell>{t('visitor.checkInTime')}</TableCell>
                <TableCell>{t('visitor.outTime')}</TableCell>
                <TableCell>{t('visitor.status')}</TableCell>
                <TableCell>{t('visitor.approvalStatus')}</TableCell>
                <TableCell>{t('visitor.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>{t('visitor.loading')}</TableCell>
                </TableRow>
              ) : filteredVisitors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <VisitorsIcon color="disabled" sx={{ fontSize: 42, mb: 1 }} />
                    <Typography color="text.secondary">{t('visitor.noActiveVisitors')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedVisitors.map((v) => (
                  <TableRow key={v.visitorId} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={v.photoUrl || undefined} sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                          {!v.photoUrl && <PersonIcon fontSize="small" />}
                        </Avatar>
                        <Typography sx={{ fontWeight: 700 }}>{v.visitorName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{v.flatNumber}/{v.blockName}</Typography>
                      {v.parkingSlotNumber && <Typography variant="caption" color="text.secondary">Parking: {v.parkingSlotNumber}</Typography>}
                    </TableCell>
                    <TableCell>{v.purpose}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{v.inTime}</Typography>
                      {v.visitEndTime && <Typography variant="caption" color="text.secondary">Until {new Date(v.visitEndTime).toLocaleString()}</Typography>}
                    </TableCell>
                    <TableCell>{v.outTime || ""}</TableCell>
                    <TableCell>
                      <Chip
                        label={getVisitStatusPresentation(v.status).label}
                        color={getVisitStatusPresentation(v.status).color}
                        size="small"
                      />
                      {renderVisitStatusControls(v)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 148, px: 1.5, verticalAlign: 'middle' }}>
                      <Stack alignItems="center">
                        <Chip
                          label={getApprovalStatusPresentation(v.approvalStatus).label}
                          color={getApprovalStatusPresentation(v.approvalStatus).color}
                          size="small"
                          variant="outlined"
                        />
                        {renderApprovalControls(v)}
                      </Stack>
                    </TableCell> {/* Show approvalStatus */}
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setEditingVisitor(v);
                          setEditDialogOpen(true);
                          setEditError('');
                        }}
                      >
                        {t('visitor.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && filteredVisitors.length > visitorsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} color="primary" />
        </Box>
      )}
      <VisitorDialog
        open={addDialogOpen}
        onClose={() => { 
          setAddDialogOpen(false); 
          setAddError(""); 
        }}
        onSubmit={async (data) => {
          setAdding(true);
          setAddError("");
          try {
            const createResponse = await visitorsAPI.create({
              ...data
            });
            setAddDialogOpen(false);
            const createdVisitor = createResponse.data?.visitor || {
              ...data,
              visitorId: createResponse.data?.visitorId,
              visitorID: createResponse.data?.visitorId,
              status: 'CREATED',
              approvalStatus: 'PENDING',
            };

            // Refresh from the API, then preserve the newly created visitor if
            // the eventually consistent flat index has not exposed it yet.
            await refreshVisitors(buildingId);
            if (createdVisitor.visitorId) {
              setVisitors(current => {
                const withoutDuplicate = current.filter(visitor =>
                  (visitor.visitorId || visitor.visitorID) !== createdVisitor.visitorId
                );
                return [createdVisitor, ...withoutDuplicate];
              });
            }
            await refreshParkingSlots(buildingId);
          } catch (err) {
            setAddError(err.response?.data?.message || "Failed to add visitor.");
          }
          setAdding(false);
        }}
        isLoading={adding}
        error={addError}
        buildingId={buildingId}
        flats={flats}
        parkingSlots={parkingSlots}
        canUseCamera={canUseCamera}
        canUseLeaveAtGate={canUseLeaveAtGate}
        canChangeApproval={canChangeApproval}
        canChangeVisitStatus={canChangeVisitStatus}
        cameraAPI={cameraAPI}
        cameraInitialized={cameraInitialized}
      />
      <VisitorDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditError(""); setEditingVisitor(null); }}
        onSubmit={async (data) => {
          setEditing(true);
          setEditError("");
          try {
            await visitorsAPI.update(buildingId, editingVisitor.visitorId || editingVisitor.visitorID, {
              ...editingVisitor,
              ...data, // <-- this includes the updated status and outTime
              buildingId,
            });
            setEditDialogOpen(false);
            setEditingVisitor(null);
            // Refresh visitors using reusable function
            await refreshVisitors(buildingId);
            await refreshParkingSlots(buildingId);
          } catch (err) {
            setEditError(err.response?.data?.message || "Failed to update visitor.");
          }
          setEditing(false);
        }}
        isLoading={editing}
        error={editError}
        buildingId={buildingId}
        flats={flats}
        parkingSlots={parkingSlots}
        initialData={editingVisitor}
        canUseCamera={canUseCamera}
        canUseLeaveAtGate={canUseLeaveAtGate}
        canChangeApproval={canChangeApproval}
        canChangeVisitStatus={canChangeVisitStatus}
        cameraAPI={cameraAPI}
        cameraInitialized={cameraInitialized}
      />
      <Dialog open={parkingDialogOpen} onClose={() => setParkingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Visitor Parking Slots</DialogTitle>
        <DialogContent>
          {parkingError && <Alert severity="error" sx={{ mb: 2 }}>{parkingError}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' }, gap: 1, alignItems: 'center', mb: 2 }}>
            <TextField label="Slot Number" value={newSlotNumber} onChange={event => setNewSlotNumber(event.target.value)} size="small" />
            <TextField label="Location" value={newSlotLocation} onChange={event => setNewSlotLocation(event.target.value)} size="small" />
            <Button
              variant="contained"
              onClick={async () => {
                if (!newSlotNumber.trim()) return;
                try {
                  await visitorsAPI.createParkingSlot({ buildingId, slotNumber: newSlotNumber, location: newSlotLocation });
                  setNewSlotNumber('');
                  setNewSlotLocation('');
                  setParkingError('');
                  await refreshParkingSlots(buildingId);
                } catch (error) {
                  setParkingError(error.response?.data?.message || 'Failed to create parking slot');
                }
              }}
            >
              Add
            </Button>
          </Box>
          <Stack spacing={1}>
            {(Array.isArray(parkingSlots) ? parkingSlots : []).map(slot => (
              <Paper key={slot.slotId} variant="outlined" sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography fontWeight={700}>{slot.slotNumber}</Typography>
                  <Typography variant="caption" color="text.secondary">{slot.location || 'No location'} - {slot.isAvailable ? 'Available' : 'Occupied'}</Typography>
                </Box>
                <IconButton
                  aria-label={`Delete parking slot ${slot.slotNumber}`}
                  disabled={!slot.isAvailable}
                  onClick={async () => {
                    try {
                      await visitorsAPI.deleteParkingSlot(buildingId, slot.slotId);
                      await refreshParkingSlots(buildingId);
                    } catch (error) {
                      setParkingError(error.response?.data?.message || 'Failed to delete parking slot');
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Paper>
            ))}
            {(!Array.isArray(parkingSlots) || parkingSlots.length === 0) && <Typography color="text.secondary">No visitor parking slots configured.</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setParkingDialogOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

// --- VisitorDialog with robust camera preview ---
const VisitorDialog = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  error,
  buildingId,
  flats,
  parkingSlots = [],
  initialData,
  canUseCamera = false,
  canUseLeaveAtGate = false,
  canChangeApproval = false,
  canChangeVisitStatus = false,
  cameraAPI,
  cameraInitialized,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  console.log("VisitorDialog: Props received - buildingId:", buildingId, "flats:", flats, "flats.length:", flats?.length);
  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const [photo, setPhoto] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const [notesError, setNotesError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const purposeOptions = [
    {
      value: 'Standard Visitors',
      label: t('visitor.purposeStandardVisitors'),
      Icon: StandardVisitorIcon,
      color: '#2563EB',
      tint: '#EFF6FF',
    },
    {
      value: 'Delivery Personnel',
      label: t('visitor.purposeDeliveryPersonnel'),
      Icon: DeliveryIcon,
      color: '#EA580C',
      tint: '#FFF7ED',
    },
    {
      value: 'Service Providers',
      label: t('visitor.purposeServiceProviders'),
      Icon: ServiceProviderIcon,
      color: '#7C3AED',
      tint: '#F5F3FF',
    },
    {
      value: 'Temporary Residents',
      label: t('visitor.purposeTemporaryResidents'),
      Icon: TemporaryResidentIcon,
      color: '#0F766E',
      tint: '#F0FDFA',
    },
    {
      value: 'Others',
      label: t('visitor.purposeOthers'),
      Icon: OtherPurposeIcon,
      color: '#475569',
      tint: '#F8FAFC',
    },
  ];
  const [formData, setFormData] = useState(initialData || {
    visitorId: initialData?.visitorId || '',
    buildingId: buildingId || '',
    visitorName: "",
    flatId: "",
    flatNumber: "",
    blockName: "",
    purpose: "",
    phoneNumber: "",
    inTime: getCurrentDateTimeLocal(),
    status: "CREATED",
    approvalStatus: "PENDING",
    vehicleNumber: "",
    idType: "",
    idNumber: "",
    notes: "",
    durationValue: 1,
    durationUnit: "HOURS",
    parkingRequired: false,
    parkingSlotId: "",
    parkingSlotNumber: ""
  });

  // Camera refs and state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState(null);

  // Assign stream to video element when both are ready
  useEffect(() => {
    if (cameraActive && stream && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = stream;
      
      // Enhanced mobile video handling
      video.playsInline = true;
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      
      // Handle video loading and playback
      const playVideo = async () => {
        try {
          await video.play();
        } catch (error) {
          console.warn('Video autoplay failed:', error);
          // This is common on mobile, user will need to interact first
        }
      };
      
      if (video.readyState >= 2) {
        playVideo();
      } else {
        video.addEventListener('loadeddata', playVideo, { once: true });
      }
    }
  }, [cameraActive, stream]);

  // Cleanup camera stream on dialog close or unmount
  useEffect(() => {
    if (!open && stream) {
      stream.getTracks().forEach(track => track.stop());
      setCameraActive(false);
      setStream(null);
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line
  }, [open, stream]);

  // Reset photo when dialog closes
  useEffect(() => {
    if (!open) {
      setPhoto(null);
      setPhotoError('');
      setNotesError('');
      setFieldErrors({});
    }
  }, [open]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        visitorId: initialData.visitorId || '',
        buildingId: initialData.buildingId || buildingId || '',
        visitorName: initialData.visitorName || '',
        flatId: initialData.flatId || initialData.flatID || '',
        flatNumber: initialData.flatNumber || '',
        blockName: initialData.blockName || '',
        purpose: initialData.purpose || '',
        phoneNumber: initialData.phoneNumber || '',
        inTime: initialData.inTime || getCurrentDateTimeLocal(),
        status: initialData.status || 'CREATED',
        approvalStatus: initialData.approvalStatus || 'PENDING',
        vehicleNumber: initialData.vehicleNumber || '',
        idType: initialData.idType || '',
        idNumber: initialData.idNumber || '',
        notes: initialData.notes || '',
        durationValue: initialData.durationValue || 1,
        durationUnit: initialData.durationUnit || 'HOURS',
        parkingRequired: Boolean(initialData.parkingSlotId),
        parkingSlotId: initialData.parkingSlotId || '',
        parkingSlotNumber: initialData.parkingSlotNumber || ''
      });
    } else if (open) {
      setFormData({
        visitorId: '',
        buildingId: buildingId || '',
        visitorName: "",
        flatId: "",
        flatNumber: "",
        blockName: "",
        purpose: "",
        phoneNumber: "",
        inTime: getCurrentDateTimeLocal(),
        status: "CREATED",
        approvalStatus: "PENDING",
        vehicleNumber: "",
        idType: "",
        idNumber: "",
        notes: "",
        durationValue: 1,
        durationUnit: "HOURS",
        parkingRequired: false,
        parkingSlotId: "",
        parkingSlotNumber: ""
      });
      // Reset photo when dialog opens for new visitor
      setPhoto(null);
      setPhotoError('');
    }
  }, [initialData, open, buildingId]);

  const handleChange = (field) => (e) => {
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    if (field === "flatId") {
      const selectedFlat = (flats || []).find(f => f.flatId === e.target.value);
      setFormData((prev) => ({
        ...prev,
        flatId: e.target.value,
        flatNumber: selectedFlat ? selectedFlat.flatNumber : "",
        blockName: selectedFlat ? selectedFlat.blockName : ""
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    const visitorName = formData.visitorName.trim();
    const phoneNumber = formData.phoneNumber.trim();
    if (!visitorName) {
      errors.visitorName = 'Visitor name is required.';
    } else if (!/^[\p{L} ]+$/u.test(visitorName)) {
      errors.visitorName = 'Visitor name can contain letters and spaces only.';
    }
    if (phoneNumber && !/^\d+$/.test(phoneNumber)) {
      errors.phoneNumber = 'Phone number can contain numbers only.';
    }
    if (!formData.purpose) {
      errors.purpose = 'Purpose is required.';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    if (formData.purpose === "Others" && !formData.notes.trim()) {
      setNotesError("Notes are required when purpose is Others.");
      return;
    }
    if (photoError) return;

    let payload = {
      ...formData,
      // datetime-local has no timezone. Convert it in the browser, where the
      // user's local timezone is known, before sending it to Lambda.
      inTime: new Date(formData.inTime).toISOString(),
    };
    if (photo && typeof photo === "string") {
      payload.photo = photo;
    }
    if (!Number(formData.durationValue) || Number(formData.durationValue) <= 0) {
      setNotesError('Visitor duration must be greater than zero.');
      return;
    }
    if (formData.parkingRequired && !formData.parkingSlotId) {
      setNotesError('Select a parking slot or turn off parking allocation.');
      return;
    }
    // If status is OUT, set outTime to now
    if (canChangeVisitStatus && formData.status === "OUT" && String(initialData?.status || '').toUpperCase().replace(/[ -]+/g, '_') !== "OUT") {
      payload.outTime = new Date().toISOString();
    }
    setNotesError('');
    onSubmit(payload);
  };

  // Helper function to check camera permissions
  const checkCameraPermission = async () => {
    if (!cameraAPI) {
      console.warn('Camera API not initialized');
      return 'denied';
    }

    try {
      // For native apps, use Capacitor's permission system
      if (cameraAPI.Capacitor.isNativePlatform()) {
        const permission = await cameraAPI.Camera.checkPermissions();
        return permission.camera === 'granted' ? 'granted' : 'denied';
      }
      
      // For web, use browser permissions API
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'camera' });
        return permission.state;
      }
      return 'unknown';
    } catch (error) {
      console.warn('Permission check failed:', error);
      return 'unknown';
    }
  };

  // --- IMPROVED CAMERA LOGIC FOR NATIVE AND WEB ---
  const startCamera = async () => {
    if (!cameraAPI) {
      setPhotoError('Camera not available');
      return;
    }

    setPhotoError('');
    setCameraActive(true);
    
    try {
      // For native platforms, use Capacitor Camera
      if (cameraAPI.Capacitor.isNativePlatform()) {
        // Request permissions if needed
        const permission = await cameraAPI.Camera.checkPermissions();
        if (permission.camera !== 'granted') {
          const requested = await cameraAPI.Camera.requestPermissions();
          if (requested.camera !== 'granted') {
            throw new Error('Camera permission denied');
          }
        }
        
        // No need to start stream for native camera - it's handled by the capture function
        return;
      }

      // Web fallback - existing getUserMedia logic
      const permissionState = await checkCameraPermission();
      if (permissionState === 'denied') {
        throw new Error('Camera permission denied');
      }

      // Enhanced camera constraints for mobile devices
      const constraints = {
        video: {
          facingMode: 'environment', // Use back camera by default
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          aspectRatio: { ideal: 16/9 }
        },
        audio: false
      };

      // Check if device supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this device');
      }

      // Request camera access with better error handling
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      
      // Additional mobile-specific handling
      if (videoRef.current) {
        videoRef.current.playsInline = true; // Important for iOS
        videoRef.current.muted = true; // Required for autoplay on mobile
        videoRef.current.setAttribute('playsinline', ''); // iOS Safari requirement
        videoRef.current.setAttribute('webkit-playsinline', ''); // Older iOS versions
      }
      
    } catch (err) {
      console.error('Camera access error:', err);
      
      // Provide specific error messages
      let errorMessage = "Unable to access camera.";
      if (err.name === 'NotAllowedError' || err.message.includes('denied')) {
        errorMessage = cameraAPI?.Capacitor.isNativePlatform() 
          ? "Camera permission denied. Please enable camera access in your device settings."
          : "Camera permission denied. Please enable camera access in your browser settings and reload the page.";
      } else if (err.name === 'NotFoundError') {
        errorMessage = "No camera found on this device.";
      } else if (err.name === 'NotSupportedError') {
        errorMessage = "Camera not supported on this device.";
      } else if (err.name === 'OverconstrainedError') {
        // Try again with basic constraints
        try {
          const basicStream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: 'environment' }, 
            audio: false 
          });
          setStream(basicStream);
          return; // Success with basic constraints
        } catch (basicErr) {
          errorMessage = "Camera constraints not supported. Try using a different device.";
        }
      }
      
      setPhotoError(errorMessage);
      setCameraActive(false);
    }
  };

  const capturePhoto = async () => {
    if (!cameraAPI) {
      setPhotoError('Camera not available');
      return;
    }

    try {
      // For native platforms, use Capacitor Camera
      if (cameraAPI.Capacitor.isNativePlatform()) {
        const photo = await cameraAPI.Camera.getPhoto({
          quality: 80,
          allowEditing: false,
          resultType: cameraAPI.CameraResultType.DataUrl,
          source: cameraAPI.CameraSource.Camera
        });
        
        setPhoto(photo.dataUrl);
        setCameraActive(false); // Close camera after capture
        return;
      }

      // Web fallback - existing canvas logic
      if (videoRef.current && canvasRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        
        // Get actual video dimensions
        const videoWidth = video.videoWidth || video.clientWidth;
        const videoHeight = video.videoHeight || video.clientHeight;
        
        // Set canvas size to match video
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        
        // Draw the video frame to canvas
        ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
        
        // Convert to data URL with good quality
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        setPhoto(dataUrl);
        
        // Stop camera after capture
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setCameraActive(false);
          setStream(null);
        }
      } else {
        setPhotoError("Camera not ready. Please try again.");
      }
    } catch (error) {
      console.error('Photo capture error:', error);
      setPhotoError("Failed to capture photo. Please try again.");
    }
  };

  return (
    <Dialog 
      className="visitor-form-dialog"
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          padding: 0,
          overflow: 'hidden'
        },
        '& .MuiDialog-container': {
          padding: 0
        }
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: '600px' },
          height: { xs: '100vh', sm: 'auto' },
          maxHeight: { xs: '100vh', sm: '88vh' },
          margin: { xs: 0, sm: 'auto' },
          borderRadius: { xs: 0, sm: 2 },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          paddingBottom: 0,
          overflow: 'hidden',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'transparent'
          }
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          px: { xs: 2, sm: 3 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          backgroundColor: '#f7f9fc',
          zIndex: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            <VisitorsIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {initialData ? t('visitor.editVisitor') : t('visitor.addVisitor')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Capture visitor, flat, approval, and identity details.
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent 
        sx={{ 
          p: { xs: 2, sm: 3 },
          overflowY: 'auto',
          flex: '1 1 auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.5, sm: 2 },
            flex: 1
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: { xs: 1, sm: 2 } }}>
              {error}
            </Alert>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Visitor Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label={t('visitor.visitorName')}
            value={formData.visitorName}
            onChange={handleChange("visitorName")}
            error={Boolean(fieldErrors.visitorName)}
            helperText={fieldErrors.visitorName}
            fullWidth
            required
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            options={flats || []}
            value={(flats || []).find(flat => flat.flatId === formData.flatId) ||
              (formData.flatId ? {
                flatId: formData.flatId,
                flatNumber: formData.flatNumber,
                blockName: formData.blockName,
              } : null)}
            onChange={(event, selectedFlat) => {
              setFieldErrors(prev => ({ ...prev, flatId: '' }));
              setFormData(prev => ({
                ...prev,
                flatId: selectedFlat?.flatId || '',
                flatNumber: selectedFlat?.flatNumber || '',
                blockName: selectedFlat?.blockName || '',
              }));
            }}
            getOptionLabel={(flat) => `${flat.flatNumber || ''} ${flat.blockName ? `(${flat.blockName})` : ''}`.trim()}
            isOptionEqualToValue={(option, value) => option.flatId === value.flatId}
            disabled={Boolean(initialData)}
            noOptionsText={t('visitor.noFlatsAvailable')}
            autoHighlight
            openOnFocus
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('visitor.flat')}
                required
                margin="dense"
                error={Boolean(fieldErrors.flatId)}
                helperText={fieldErrors.flatId}
                placeholder="Search by flat or block"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '14px', sm: '16px' }
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <FlatIcon fontSize="small" color="action" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <FormControl
            component="fieldset"
            required
            error={Boolean(fieldErrors.purpose)}
            sx={{
              gridColumn: '1 / -1',
              mt: 0.5,
            }}
          >
            <FormLabel
              component="legend"
              sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}
            >
              {t('visitor.purpose')}
            </FormLabel>
            <RadioGroup
              aria-label={t('visitor.purpose')}
              name="visitor-purpose"
              value={formData.purpose}
              onChange={handleChange("purpose")}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(5, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {purposeOptions.map(({ value, label, Icon, color, tint }) => {
                const selected = formData.purpose === value;
                const labelParts = label.match(/^(.+?)\s*\((.+)\)$/);
                const title = labelParts?.[1] || label;
                const description = labelParts?.[2] || '';
                return (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio sx={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />}
                    label={
                      <Stack alignItems="center" spacing={1} sx={{ width: '100%', height: '100%' }}>
                        {selected && (
                          <SelectedPurposeIcon
                            aria-hidden="true"
                            sx={{
                              position: 'absolute',
                              top: 7,
                              right: 7,
                              color,
                              bgcolor: 'background.paper',
                              borderRadius: '50%',
                              fontSize: 21,
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            width: 54,
                            height: 54,
                            borderRadius: 2.5,
                            display: 'grid',
                            placeItems: 'center',
                            color: selected ? '#fff' : color,
                            background: selected
                              ? `linear-gradient(145deg, ${color}, ${color}CC)`
                              : tint,
                            boxShadow: selected
                              ? `0 7px 16px ${color}3D`
                              : `inset 0 0 0 1px ${color}12`,
                            transform: selected ? 'translateY(-2px) scale(1.04)' : 'none',
                            transition: 'transform 150ms, box-shadow 150ms',
                          }}
                        >
                          <Icon sx={{ fontSize: 29 }} />
                        </Box>
                        <Typography
                          variant="body2"
                          align="center"
                          sx={{
                            lineHeight: 1.2,
                            fontWeight: 700,
                            color: selected ? color : 'text.primary',
                          }}
                        >
                          {title}
                        </Typography>
                        {description && (
                          <Typography
                            variant="caption"
                            align="center"
                            sx={{
                              lineHeight: 1.25,
                              color: 'text.secondary',
                              mt: '0 !important',
                            }}
                          >
                            {description}
                          </Typography>
                        )}
                      </Stack>
                    }
                    sx={{
                      position: 'relative',
                      m: 0,
                      minWidth: 0,
                      minHeight: 144,
                      px: 1,
                      py: 1.5,
                      alignItems: 'stretch',
                      justifyContent: 'center',
                      border: 2,
                      borderColor: selected ? color : 'divider',
                      borderRadius: 2.5,
                      bgcolor: selected ? tint : 'background.paper',
                      boxShadow: selected
                        ? `0 8px 22px ${color}1F`
                        : '0 2px 5px rgba(15, 23, 42, 0.05)',
                      cursor: 'pointer',
                      transition: 'border-color 150ms, background-color 150ms, box-shadow 150ms, transform 150ms',
                      '&:hover': {
                        borderColor: color,
                        bgcolor: tint,
                        boxShadow: `0 7px 18px ${color}1A`,
                        transform: 'translateY(-2px)',
                      },
                      '&:focus-within': {
                        boxShadow: `0 0 0 3px ${color}38`,
                      },
                      '& .MuiFormControlLabel-label': {
                        width: '100%',
                      },
                    }}
                  />
                );
              })}
            </RadioGroup>
            {fieldErrors.purpose && <FormHelperText>{fieldErrors.purpose}</FormHelperText>}
          </FormControl>
          <TextField
            label={t('visitor.phoneNumber')}
            value={formData.phoneNumber}
            onChange={handleChange("phoneNumber")}
            error={Boolean(fieldErrors.phoneNumber)}
            helperText={fieldErrors.phoneNumber}
            fullWidth
            margin="dense"
            type="tel"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          </Box>

          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Visit Status
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label={t('visitor.checkInTime')}
            value={formData.inTime}
            onChange={handleChange("inTime")}
            fullWidth
            margin="dense"
            type="datetime-local"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TimeIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Visit Duration"
            value={formData.durationValue}
            onChange={handleChange("durationValue")}
            type="number"
            required
            inputProps={{ min: 0.25, step: 0.25 }}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Duration Unit"
            value={formData.durationUnit}
            onChange={handleChange("durationUnit")}
            select
            required
            fullWidth
            margin="dense"
          >
            <MenuItem value="HOURS">Hours</MenuItem>
            <MenuItem value="DAYS">Days</MenuItem>
          </TextField>
          <TextField
            label="Expected End Time"
            value={(() => {
              const start = new Date(formData.inTime);
              const duration = Number(formData.durationValue);
              if (Number.isNaN(start.getTime()) || !duration) return '';
              const milliseconds = duration * (formData.durationUnit === 'DAYS' ? 86400000 : 3600000);
              return new Date(start.getTime() + milliseconds).toLocaleString();
            })()}
            fullWidth
            margin="dense"
            InputProps={{ readOnly: true }}
          />
          <TextField
            label={t('visitor.status')}
            value={formData.status}
            onChange={handleChange("status")}
            disabled={!initialData || !canChangeVisitStatus}
            fullWidth
            select
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          >
            <MenuItem value="CREATED">Created</MenuItem>
            <MenuItem value="IN">{t('visitor.statusIn')}</MenuItem>
            <MenuItem value="OUT">{t('visitor.statusOut')}</MenuItem>
            <MenuItem value="LEFT_AT_GATE">Left at Gate</MenuItem>
          </TextField>
          <TextField
            label={t('visitor.approvalStatus')}
            value={formData.approvalStatus}
            onChange={handleChange("approvalStatus")}
            disabled={!initialData || !canChangeApproval || String(initialData.approvalStatus || 'PENDING').toUpperCase().replace(/[ -]+/g, '_') !== 'PENDING'}
            fullWidth
            select
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          >
            <MenuItem value="PENDING">{t('visitor.pending')}</MenuItem>
            <MenuItem value="APPROVED">{t('visitor.approved')}</MenuItem>
            <MenuItem value="REJECTED">{t('visitor.rejected')}</MenuItem>
            {canUseLeaveAtGate && formData.purpose === 'Delivery Personnel' && (
              <MenuItem value="Leave At Gate">{t('visitor.leaveAtGate')}</MenuItem>
            )}
          </TextField>
          </Box>

          <FormControlLabel
            control={(
              <Checkbox
                checked={formData.parkingRequired}
                disabled={Boolean(initialData)}
                onChange={event => setFormData(prev => ({
                  ...prev,
                  parkingRequired: event.target.checked,
                  parkingSlotId: event.target.checked ? prev.parkingSlotId : '',
                  parkingSlotNumber: event.target.checked ? prev.parkingSlotNumber : ''
                }))}
              />
            )}
            label={(
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 2,
                    color: formData.parkingRequired ? 'primary.contrastText' : 'primary.main',
                    background: formData.parkingRequired
                      ? `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                      : `${theme.palette.primary.main}14`,
                    boxShadow: formData.parkingRequired
                      ? `0 5px 14px ${theme.palette.primary.main}35`
                      : 'none',
                    transition: 'background-color 150ms, box-shadow 150ms',
                  }}
                >
                  <ParkingIcon />
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Allocate visitor parking
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reserve an available slot for this visitor
                  </Typography>
                </Box>
              </Stack>
            )}
            sx={{
              m: 0,
              alignSelf: 'flex-start',
              '& .MuiFormControlLabel-label': { ml: 0.25 },
            }}
          />
          {formData.parkingRequired && (
            <FormControl
              component="fieldset"
              required
              sx={{ width: '100%' }}
            >
              <FormLabel
                component="legend"
                sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}
              >
                Select Parking Slot
              </FormLabel>
              <Box
                role="group"
                aria-label="Select parking slot"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1,
                }}
              >
                {(Array.isArray(parkingSlots) ? parkingSlots : [])
                  .filter(slot => slot.isAvailable || slot.slotId === formData.parkingSlotId)
                  .map(slot => {
                    const selected = formData.parkingSlotId === slot.slotId;
                    return (
                      <FormControlLabel
                        key={slot.slotId}
                        disabled={Boolean(initialData)}
                        control={(
                          <Checkbox
                            checked={selected}
                            onChange={event => {
                              setNotesError('');
                              setFormData(prev => ({
                                ...prev,
                                parkingSlotId: event.target.checked ? slot.slotId : '',
                                parkingSlotNumber: event.target.checked ? slot.slotNumber : ''
                              }));
                            }}
                            sx={{
                              color: 'primary.main',
                              '&.Mui-checked': { color: 'primary.main' },
                            }}
                          />
                        )}
                        label={(
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                flexShrink: 0,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: 1.5,
                                color: selected ? 'primary.contrastText' : 'primary.main',
                                bgcolor: selected ? 'primary.main' : `${theme.palette.primary.main}14`,
                              }}
                            >
                              <ParkingIcon fontSize="small" />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {slot.slotNumber}
                              </Typography>
                              {slot.location && (
                                <Typography variant="caption" color="text.secondary">
                                  {slot.location}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        )}
                        sx={{
                          m: 0,
                          px: 1,
                          py: 0.75,
                          minHeight: 64,
                          border: 2,
                          borderColor: selected ? 'primary.main' : 'divider',
                          borderRadius: 2,
                          bgcolor: selected ? `${theme.palette.primary.main}0A` : 'background.paper',
                          boxShadow: selected ? `0 5px 14px ${theme.palette.primary.main}20` : 'none',
                          cursor: initialData ? 'default' : 'pointer',
                          transition: 'border-color 150ms, background-color 150ms, box-shadow 150ms',
                          '&:hover': initialData ? {} : {
                            borderColor: 'primary.main',
                            bgcolor: `${theme.palette.primary.main}0A`,
                          },
                          '& .MuiFormControlLabel-label': { flex: 1 },
                        }}
                      />
                    );
                  })}
              </Box>
              {(Array.isArray(parkingSlots) ? parkingSlots : []).filter(slot => slot.isAvailable).length === 0 && !formData.parkingSlotId && (
                <Alert severity="info" sx={{ mt: 0.5 }}>
                  No parking slots available
                </Alert>
              )}
              <FormHelperText>Choose one available parking slot.</FormHelperText>
            </FormControl>
          )}

          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Optional Verification
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label={t('visitor.vehicleNumber')}
            value={formData.vehicleNumber}
            onChange={handleChange("vehicleNumber")}
            fullWidth
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <VehicleIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label={t('visitor.idType')}
            value={formData.idType}
            onChange={handleChange("idType")}
            fullWidth
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IdIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label={t('visitor.idNumber')}
            value={formData.idNumber}
            onChange={handleChange("idNumber")}
            fullWidth
            margin="dense"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IdIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          </Box>
          <TextField
            label={t('visitor.notes')}
            value={formData.notes}
            onChange={handleChange("notes")}
            fullWidth
            margin="dense"
            required={formData.purpose === "Others"}
            error={!!notesError}
            helperText={notesError}
            multiline
            rows={3}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <NotesIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Camera and photo controls - only for building_admin and maintenance_staff */}
          {canUseCamera && (
            <Box sx={{ 
              mt: { xs: 1, sm: 2 },
              p: { xs: 1, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: '#f7f9fc'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CameraIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xs: '14px', sm: '16px' } }}>
                  Visitor Photo
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                mb: 2
              }}>
                {/* For native apps, show direct photo capture button */}
                {cameraAPI?.Capacitor.isNativePlatform() ? (
                  <Button 
                    variant="contained" 
                    onClick={capturePhoto} 
                    disabled={!cameraInitialized}
                    size="small"
                    sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                  >
                    {cameraInitialized ? 'Take Photo' : 'Initializing Camera...'}
                  </Button>
                ) : (
                  <>
                    <Button 
                      variant="outlined" 
                      onClick={startCamera} 
                      disabled={cameraActive || !cameraInitialized} 
                      size="small"
                      sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 700 }}
                    >
                      {!cameraInitialized ? 'Initializing...' : cameraActive ? 'Camera Active' : 'Start Camera'}
                    </Button>
                    <Button 
                      variant="outlined" 
                      onClick={capturePhoto} 
                      disabled={!cameraActive || !cameraInitialized} 
                      size="small"
                      sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 700 }}
                    >
                      Capture Photo
                    </Button>
                  </>
                )}
                {photo && (
                  <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={() => setPhoto(null)}
                    size="small"
                    sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                  >
                    Clear Photo
                  </Button>
                )}
              </Box>
              {photoError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {photoError}
                </Alert>
              )}
              {/* Camera preview - only for web browsers */}
              {!cameraAPI?.Capacitor.isNativePlatform() && cameraActive && (
                <Box sx={{ mt: 1 }}>
                  <video
                    ref={videoRef}
                    width="100%"
                    height="auto"
                    autoPlay
                    muted
                    playsInline
                    webkit-playsinline="true"
                    controls={false}
                    style={{ 
                      borderRadius: 4, 
                      border: "1px solid #e0e0e0",
                      maxWidth: window.innerWidth < 600 ? '100%' : '320px',
                      maxHeight: window.innerWidth < 600 ? '300px' : '240px',
                      objectFit: 'cover'
                    }}
                  />
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                </Box>
              )}
              {/* Photo preview */}
              {photo && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <img
                    src={typeof photo === "string" ? photo : ""}
                    alt="Visitor"
                    style={{ 
                      borderRadius: 4, 
                      border: "1px solid #e0e0e0",
                      maxWidth: '100%',
                      maxHeight: '160px',
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
          
          {/* Buttons at bottom of content */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 },
            justifyContent: 'flex-end',
            mt: { xs: 2, sm: 3 },
            pt: { xs: 1.5, sm: 2 },
            borderTop: '1px solid #e0e0e0',
            bgcolor: 'background.paper',
            pb: { xs: 2, sm: 0 },
            zIndex: 1
          }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
              sx={{
                minHeight: { xs: '48px', sm: '40px' },
                fontSize: { xs: '16px', sm: '14px' },
                fontWeight: 'bold',
                borderRadius: '8px',
                order: { xs: 1, sm: 2 }
              }}
              onClick={handleSubmit}
            >
              {isLoading ? t('visitor.saving') : t('visitor.save')}
            </Button>
            
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
              sx={{
                minHeight: { xs: '48px', sm: '40px' },
                fontSize: { xs: '16px', sm: '14px' },
                borderRadius: '8px',
                order: { xs: 2, sm: 1 },
                color: '#666',
                borderColor: '#ccc',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              {t('visitor.cancel')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VisitorDashboard;
